import React, { useMemo, useState, useRef } from 'react';
import { X, Loader2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import { scheduleShiftImportApi } from '../services/api';
import type { Shift } from '../types';
import { appConfirm } from '../context/SystemDialogContext';

export interface BulkDeleteScheduleShiftsModalProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  scheduleId: number;
  /** Week start yyyy-MM-dd (inclusive) */
  weekStartDate: string;
  shifts: Shift[];
  onDeleted?: () => void;
}

function parseLocalDateTime(iso: string): Date {
  if (!iso) return new Date(NaN);
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? new Date(NaN) : d;
}

const BulkDeleteScheduleShiftsModal: React.FC<BulkDeleteScheduleShiftsModalProps> = ({
  open,
  onClose,
  locationId,
  scheduleId,
  weekStartDate,
  shifts,
  onDeleted,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deleteLockRef = useRef(false);

  const sorted = useMemo(() => {
    return [...shifts].sort((a, b) => {
      const ta = parseLocalDateTime(a.startTime).getTime();
      const tb = parseLocalDateTime(b.startTime).getTime();
      return ta - tb || a.id - b.id;
    });
  }, [shifts]);

  React.useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setError(null);
      deleteLockRef.current = false;
    }
  }, [open]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelectedIds(new Set(sorted.map((s) => s.id)));
  const unselectAll = () => setSelectedIds(new Set());

  const handleDelete = async () => {
    if (selectedIds.size === 0 || deleteLockRef.current) return;
    if (
      !(await appConfirm(
        `Permanently delete ${selectedIds.size} shift(s) for this week? This cannot be undone.`,
        { danger: true, confirmLabel: 'Delete' }
      ))
    ) {
      return;
    }
    deleteLockRef.current = true;
    setDeleting(true);
    setError(null);
    try {
      await scheduleShiftImportApi.importBulkDelete({
        locationId,
        scheduleId,
        weekStartDate,
        shiftIds: Array.from(selectedIds),
      });
      onDeleted?.();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || (err instanceof Error ? err.message : 'Delete failed.'));
    } finally {
      setDeleting(false);
      deleteLockRef.current = false;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Bulk delete shifts (this week)</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          <p className="text-sm text-gray-600">
            Select shifts for the current schedule and week, then delete. Week starts {weekStartDate}.
          </p>

          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50"
            >
              Select all ({sorted.length})
            </button>
            <button type="button" onClick={unselectAll} className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50">
              Clear selection
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded border border-gray-200">
            {sorted.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No shifts in this week for the selected schedule.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="w-10 px-2 py-2" />
                    <th className="px-2 py-2 text-left">Employee</th>
                    <th className="px-2 py-2 text-left">Start</th>
                    <th className="px-2 py-2 text-left">End</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s) => {
                    const st = parseLocalDateTime(s.startTime);
                    const en = parseLocalDateTime(s.endTime);
                    const name =
                      s.employee?.firstName != null || s.employee?.lastName != null
                        ? `${s.employee?.firstName ?? ''} ${s.employee?.lastName ?? ''}`.trim()
                        : `Employee #${s.employeeId}`;
                    return (
                      <tr key={s.id} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(s.id)}
                            onChange={() => toggle(s.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-2 py-1.5">{name}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {!Number.isNaN(st.getTime()) ? `${format(st, 'EEE')} ${formatCompanyDateTime(st)}` : s.startTime}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {!Number.isNaN(en.getTime()) ? `${format(en, 'EEE')} ${formatCompanyDateTime(en)}` : s.endTime}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <button type="button" onClick={onClose} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting || selectedIds.size === 0}
              className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? 'Deleting…' : `Delete selected (${selectedIds.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteScheduleShiftsModal;
